import React, { useEffect, useState } from "react";
import { PublicKey } from "@solana/web3.js";
import { cpmmPrograms } from "@oxminter/shared/feeconfig";
import { useApp } from "../state";
import { Note } from "../components/common";
import {
  buildGuide,
  guideProgress,
  metadataTemplate,
  type GuideSnapshot,
  type GuideStep,
} from "../lib/guide";

/**
 * A walkthrough that reads the same state everything else does.
 *
 * It explains and it checks. It never fills in a field and never asks for a
 * signature: in a tool where a wrong decimal is permanent, the person who
 * types a value should be the person who chose it. Every "done" below was
 * observed — an account read, a balance, a journal entry — not inferred from
 * the user having visited a tab.
 */

const STATE_LABEL: Record<GuideStep["state"], string> = {
  done: "Done",
  current: "Do this next",
  blocked: "Waiting",
  warn: "Check this",
  skipped: "Not in this run",
};

const STATE_PILL: Record<GuideStep["state"], string> = {
  done: "ok",
  current: "",
  blocked: "",
  warn: "warn",
  skipped: "",
};

export default function Guide() {
  const { connection, net, rpcUrl, wallet, token, launchStatus } = useApp();

  const [rpcReachable, setRpcReachable] = useState<boolean | null>(null);
  const [rpcVersion, setRpcVersion] = useState<string | undefined>(undefined);
  const [cpmmPresent, setCpmmPresent] = useState<boolean | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  // Is this endpoint the network it claims to be? Reading the CPMM program for
  // the SELECTED cluster answers that without hardcoding a genesis hash: the
  // devnet program id simply does not exist on mainnet, or the reverse.
  useEffect(() => {
    let live = true;
    setRpcReachable(null);
    setCpmmPresent(null);
    setRpcVersion(undefined);

    connection
      .getVersion()
      .then((v) => {
        if (!live) return;
        setRpcReachable(true);
        setRpcVersion(v["solana-core"]);
        return connection.getAccountInfo(
          cpmmPrograms(net.cluster).programId as PublicKey,
          "confirmed"
        );
      })
      .then((info) => {
        if (!live || info === undefined) return;
        setCpmmPresent(Boolean(info && info.executable));
      })
      .catch(() => {
        if (!live) return;
        setRpcReachable(false);
        setCpmmPresent(false);
      });

    return () => {
      live = false;
    };
  }, [connection, net.cluster, rpcUrl]);

  useEffect(() => {
    if (!wallet) return setBalance(null);
    let live = true;
    const tick = () =>
      connection
        .getBalance(wallet.publicKey, "confirmed")
        .then((b) => live && setBalance(b))
        .catch(() => live && setBalance(null));
    void tick();
    const id = setInterval(tick, 20000);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, [wallet, connection]);

  const snapshot: GuideSnapshot = {
    cluster: net.cluster,
    networkLabel: net.label,
    isMainnet: Boolean(net.danger),
    rpcReachable,
    rpcVersion,
    cpmmProgramPresent: cpmmPresent,
    walletConnected: Boolean(wallet),
    walletLabel: wallet ? `${wallet.name} · ${wallet.publicKey.toBase58()}` : undefined,
    balanceLamports: balance,
    requiredLamports: launchStatus ? launchStatus.requiredLamports : null,
    launch: launchStatus,
    hasToken: Boolean(token),
    hasPool: Boolean(token?.poolId),
    lpBalanceRaw: null,
  };

  const steps = buildGuide(snapshot);
  const progress = guideProgress(steps);
  const phases: string[] = [];
  steps.forEach((s) => {
    if (!phases.includes(s.phase)) phases.push(s.phase);
  });

  const template = metadataTemplate("", "");

  return (
    <>
      <div className="card">
        <h2>Walkthrough</h2>
        <p className="sub">
          Every step below is checked against the same state the rest of the console reads — the
          chain, your wallet, the launch journal. Nothing here fills in a field or asks for a
          signature; it tells you what to do next, and what it costs you if you get it wrong.
        </p>

        <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>
            {progress.done} of {progress.total} steps that apply to this run
          </span>
          <span style={{ fontSize: 12, color: "var(--muted)" }} data-testid="guide-progress">
            {progress.done}/{progress.total}
          </span>
        </div>
        <div className="bar">
          <i style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
        </div>

        {net.danger ? (
          <Note kind="danger">
            <p>
              You are on mainnet-beta. Do one complete launch on devnet or a local validator
              first — including a swap — before doing anything here.
            </p>
          </Note>
        ) : null}
      </div>

      {phases.map((phase) => (
        <div className="card" key={phase}>
          <h2>{phase}</h2>
          <ol className="guide">
            {steps
              .filter((s) => s.phase === phase)
              .map((s) => (
                <li key={s.id} data-state={s.state} data-testid={`guide-${s.id}`}>
                  <div className="guide-head">
                    <span className="guide-title">{s.title}</span>
                    <span className={`pill ${STATE_PILL[s.state]}`} data-testid={`guide-state-${s.id}`}>
                      {STATE_LABEL[s.state]}
                    </span>
                    {s.permanent ? <span className="req req-irreversible">Irreversible</span> : null}
                    {s.tab ? <span className="guide-tab">{s.tab}</span> : null}
                  </div>
                  <p className="guide-action">{s.action}</p>
                  <p className="guide-why">{s.why}</p>
                  {s.detail ? (
                    <p className="guide-detail" data-testid={`guide-detail-${s.id}`}>
                      {s.detail}
                    </p>
                  ) : null}
                </li>
              ))}
          </ol>
        </div>
      ))}

      <div className="card">
        <h2>The metadata file, which is the step people skip</h2>
        <p className="sub">
          Nothing on chain stores your token&apos;s picture. The mint stores a URI, and every
          wallet fetches this JSON from it. Host it somewhere that will still answer in a year —
          IPFS or Arweave, not a personal server — then paste the URI into the Launch tab and
          press <strong>Test URI</strong>.
        </p>
        <div className="log" style={{ maxHeight: "none" }} data-testid="metadata-template">
          {template.split("\n").map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
        <div className="row" style={{ marginTop: 10 }}>
          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(template);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              } catch {
                setCopied(false);
              }
            }}
            data-testid="btn-copy-template"
          >
            {copied ? "Copied" : "Copy template"}
          </button>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>
            <code>image</code> must be a direct link to the file, not to a page showing it.
          </span>
        </div>
      </div>

      <div className="card">
        <h2>What nobody can undo for you</h2>
        <ul>
          <li>
            <strong>Decimals and supply</strong> are set when the mint is created. There is no
            later edit — only a new token.
          </li>
          <li>
            <strong>A revoked authority stays revoked.</strong> Not by you, not by Raydium, not by
            Solana.
          </li>
          <li>
            <strong>Frozen metadata is frozen.</strong> If the URI dies afterwards, the token is a
            blank square permanently.
          </li>
          <li>
            <strong>Burned tokens and burned LP are gone.</strong> Burn &amp; Earn also locks
            liquidity forever; it just keeps the fees claimable through an NFT.
          </li>
          <li>
            <strong>Metadata is written before authorities are revoked</strong>, and the console
            enforces that order. Reversed, the Metaplex program rejects the metadata transaction
            and the token can never be named.
          </li>
        </ul>
        <Note>
          <p>
            None of this is legal or financial advice. Issuing a token can create securities, tax
            and advertising obligations that vary by jurisdiction, and a careful interface does not
            change that.
          </p>
        </Note>
      </div>
    </>
  );
}
